import { useEffect, useState } from 'react';
import { invokeAgentFabReadModel } from '@/lib/readModels';
import { useDeferredActivation } from '@/hooks/useDeferredActivation';

interface UseAgentFabAvailabilityOptions {
  organizationId?: string;
  agentOrganizationId?: string;
  userId?: string;
}

const agentFabAvailabilityCache = new Map<string, { agentEnabled: boolean; expiresAt: number }>();
const agentFabAvailabilityInflight = new Map<string, Promise<boolean>>();
const AGENT_FAB_AVAILABILITY_TTL_MS = 30_000;

function buildCacheKey(options: UseAgentFabAvailabilityOptions) {
  return `${options.organizationId ?? 'none'}:${options.agentOrganizationId ?? 'none'}:${options.userId ?? 'none'}`;
}

async function fetchAgentFabAvailability(
  options: Required<Pick<UseAgentFabAvailabilityOptions, 'organizationId'>> & {
    agentOrganizationId?: string;
  },
) {
  const cacheKey = buildCacheKey(options);
  const cached = agentFabAvailabilityCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.agentEnabled;
  }

  let inflight = agentFabAvailabilityInflight.get(cacheKey);
  if (!inflight) {
    inflight = invokeAgentFabReadModel(options, {
      expectedUserId: options.userId,
    })
      .then((response) => {
        agentFabAvailabilityCache.set(cacheKey, {
          agentEnabled: response.agentEnabled,
          expiresAt: Date.now() + AGENT_FAB_AVAILABILITY_TTL_MS,
        });
        return response.agentEnabled;
      })
      .finally(() => {
        agentFabAvailabilityInflight.delete(cacheKey);
      });
    agentFabAvailabilityInflight.set(cacheKey, inflight);
  }

  return inflight;
}

export function resetAgentFabAvailabilityCacheForTests() {
  agentFabAvailabilityCache.clear();
  agentFabAvailabilityInflight.clear();
}

export function useAgentFabAvailability(options: UseAgentFabAvailabilityOptions) {
  const isActivated = useDeferredActivation(Boolean(options.organizationId), 400);
  const [state, setState] = useState<{ agentEnabled: boolean; isLoading: boolean }>(() => {
    if (!options.organizationId) {
      return { agentEnabled: false, isLoading: false };
    }

    const cached = agentFabAvailabilityCache.get(buildCacheKey(options));
    if (cached && cached.expiresAt > Date.now()) {
      return { agentEnabled: cached.agentEnabled, isLoading: false };
    }

    return { agentEnabled: false, isLoading: true };
  });

  useEffect(() => {
    if (!options.organizationId) {
      setState({ agentEnabled: false, isLoading: false });
      return;
    }

    if (!options.userId) {
      setState({ agentEnabled: false, isLoading: false });
      return;
    }

    if (!isActivated) {
      setState((current) => ({ ...current, isLoading: true }));
      return;
    }

    let isCancelled = false;
    void fetchAgentFabAvailability({
      organizationId: options.organizationId,
      agentOrganizationId: options.agentOrganizationId,
      userId: options.userId,
    })
      .then((agentEnabled) => {
        if (!isCancelled) {
          setState({ agentEnabled, isLoading: false });
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setState({ agentEnabled: false, isLoading: false });
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [isActivated, options.agentOrganizationId, options.organizationId, options.userId]);

  return state;
}
